interface PaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  itemLabel?: string;

  /** Number of page buttons to display */
  visiblePages?: number;

  hasPreviousPage: boolean;
  hasNextPage: boolean;

  onPrevious: () => void;
  onNext: () => void;
  onPageChange: (page: number) => void;
}

const Pagination = ({
  page,
  totalPages,
  totalItems,
  itemsPerPage,
  itemLabel = "items",
  visiblePages = 5,
  hasPreviousPage,
  hasNextPage,
  onPrevious,
  onNext,
  onPageChange,
}: PaginationProps) => {
  const startItem = totalItems === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const endItem = Math.min(page * itemsPerPage, totalItems);

  const startPage = Math.max(
    1,
    Math.min(
      page - Math.floor(visiblePages / 2),
      totalPages - visiblePages + 1
    )
  );

  const endPage = Math.min(
    totalPages,
    startPage + visiblePages - 1
  );

  const pageNumbers = Array.from(
    { length: Math.max(0, endPage - startPage + 1) },
    (_, index) => startPage + index
  );

  return (
    <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
      <p className="text-sm text-slate-500">
        Showing{" "}
        <span className="font-medium text-slate-700">
          {startItem}–{endItem}
        </span>{" "}
        of{" "}
        <span className="font-medium text-slate-700">
          {totalItems}
        </span>{" "}
        {itemLabel}
      </p>

      <div className={`flex items-center ${visiblePages === 0 ? "gap-3" : "gap-1"}`}>
        <button
          type="button"
          disabled={!hasPreviousPage}
          onClick={onPrevious}
          className={`h-9 rounded-lg border px-3 text-sm transition ${hasPreviousPage
              ? "border-slate-200 text-slate-600 hover:bg-slate-50"
              : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300"
            }`}
        >
          Previous
        </button>

        {visiblePages > 0 &&
          pageNumbers.map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              onClick={() => onPageChange(pageNumber)}
              className={`h-9 min-w-9 rounded-lg px-3 text-sm font-medium transition ${page === pageNumber
                  ? "bg-blue-600 text-white"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
            >
              {pageNumber}
            </button>
          ))}

        <button
          type="button"
          disabled={!hasNextPage}
          onClick={onNext}
          className={`h-9 rounded-lg border px-3 text-sm transition ${hasNextPage
              ? "border-slate-200 text-slate-600 hover:bg-slate-50"
              : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300"
            }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;